import React from 'react';
import {
  Edit,
  SimpleForm,
  Toolbar,
  SaveButton,
  useNotify,
  useRefresh,
  useGetOne,
  Loading,
  ImageInput,
  ImageField,
  type ToolbarProps,
  TextInput,
  List,
  Datagrid,
  TextField,
  DeleteButton,
  Create,
  FileInput,
  useListContext
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List as MuiList,
  ListItem,
  ListItemText,
  Modal,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';

// カスタムツールバー (保存ボタンのみ)
const CustomToolbar = (props: ToolbarProps) => (
  <Toolbar {...props} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
    <SaveButton />
  </Toolbar>
);

// モーダルスタイル
const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
  maxWidth: { xs: 'calc(100vw - 48px)', sm: '90vw', md: '80vw' },
  maxHeight: '90vh',
  width: { xs: 'calc(100vw - 48px)', sm: 'auto' }
};

// =================================================================
// ImageManager を定義
// =================================================================
const ImageManager = () => {
  // 2MB 上限
  const MAX_SIZE_BYTES = 2 * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png'];
  const validateAvailableImages = (value: any) => {
    // 1. ファイルが1つも選ばれていない → エラー
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'アップロードする画像を選択してください。（対応形式：JPEG / PNG、容量上限：2MB）';
    }
    const files = Array.isArray(value) ? value : [value];
    for (const file of files) {
      const rawFile: File | undefined = file?.rawFile;
      // 既存レコード表示時など rawFile が無いこともあるので、その場合はスキップ
      if (!rawFile) continue;
      const { type, size, name } = rawFile;
      // 対応形式チェック
      if (!ALLOWED_TYPES.includes(type)) {
        return `ファイル「${name}」は対応形式ではありません。対応形式：JPEG / PNG`;
      }

      // 容量チェック
      if (size > MAX_SIZE_BYTES) {
        const mb = (size / 1024 / 1024).toFixed(1);
        return `ファイル「${name}」は ${mb}MB あります。容量上限：2MB までです。`;
      }
    }
    // すべてOK
    return undefined;
  };
  const notify = useNotify();
  const refresh = useRefresh(); // ページ全体のリフレッシュに使う
  const [formKey, setFormKey] = React.useState(0);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<{ src: string; title: string } | null>(null);

  const handleOpenModal = (src: string, title: string) => {
    setSelectedImage({ src, title });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const downloadBase64Image = (base64: string, filename: string) => {
    const link = document.createElement('a');
    link.href = base64;
    // ファイル名が undefined の場合、'untitled.jpg' などを推測
    let finalFilename = filename;
    if (!filename || filename === 'untitled') {
      let extension = 'jpg';
      const match = base64.match(/data:image\/(.*?);/);
      if (match && match[1]) {
        extension = match[1].split('+')[0];
      }
      finalFilename = `image.${extension}`;
    }
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card sx={{ width: '100%', mt: 4 }}>
      <CardContent>
        {/* 1. 既存の画像リスト */}
        <Typography variant="h6" gutterBottom>
          利用可能イメージ一覧
        </Typography>
        <List
          resource="client-pr-images"
          actions={false}
          pagination={false}
          component="div"
          title=" "
          empty={
            <Typography variant="body2" sx={{ m: 2 }}>
              アップロードされた画像はありません。
            </Typography>
          }
        >
          <MuiList sx={{ width: '100%' }}>
            {/* useListContext() を使ってデータを取得し、
              MUI の <ListItem> で描画します。
              <Datagrid> を使わないので "プレビュー" ヘッダーは出ません。
            */}
            <ImageList_MUI onImageClick={handleOpenModal} />
          </MuiList>
        </List>

        {/* 2. 新規アップロードフォーム */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>
          新規画像をアップロード
        </Typography>
        <Create
          key={formKey}
          resource="client-pr-images"
          component="div"
          title=" "
          redirect={false}
          mutationOptions={{
            onSuccess: () => {
              notify('画像をアップロードしました。', { type: 'info' });
              refresh();
              setFormKey((key) => key + 1);
            },
            onError: (error: any) => {
              // dataProvider が throw した Error オブジェクトからメッセージを取得
              const errorMessage = error.message || 'アップロードに失敗しました。';
              // 赤いエラー通知を表示
              notify(errorMessage, {
                type: 'error',
                // メッセージが長文でも改行して表示する
                messageArgs: { _: errorMessage },
                multiLine: true
              });
              // フォームはリセットせず、失敗したファイル（赤文字）を残す
            }
          }}
        >
          <SimpleForm
            toolbar={
              <Toolbar>
                <SaveButton label="アップロード" icon={<></>} />
              </Toolbar>
            }
          >
            <FileInput
              source="availableImages"
              label="画像をドラッグ＆ドロップ、またはクリックして選択"
              accept="image/jpeg, image/png"
              multiple={true}
              maxSize={MAX_SIZE_BYTES}
              validate={validateAvailableImages}
              placeholder={<p>ここに画像をドロップしてください (複数可, 1枚2MBまで, JPEG/PNGのみ)</p>}
            >
              <ImageField source="src" title="title" />
            </FileInput>
          </SimpleForm>
        </Create>
      </CardContent>

      {/* モーダル */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: 4
            }}
          >
            {selectedImage?.title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <Close />
          </IconButton>
          <Box sx={{ mt: 2, overflow: 'auto', maxHeight: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={selectedImage?.src}
              alt={selectedImage?.title}
              style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
            />
          </Box>
        </Box>
      </Modal>
    </Card>
  );
};

const ImageList_MUI = ({ onImageClick }: { onImageClick: (src: string, title: string) => void }) => {
  const { data, isLoading } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (isLoading) return null;

  // dataProvider が返す { id, availableImages: { src, title } } の配列
  const images: any[] = data;

  return (
    <>
      {images.map((img) => {
        // null チェック
        const imageData = img.availableImages;
        if (!imageData) return null;

        return (
          <ListItem
            key={img.id}
            sx={{
              alignItems: 'flex-start' // 上揃えにする
            }}
            // secondaryAction で右端にボタンを配置
            secondaryAction={
              <DeleteButton
                record={img} // 削除対象のレコードを指定
                resource="client-pr-images"
                mutationMode="pessimistic"
                redirect={false}
                mutationOptions={{
                  onSuccess: () => {
                    notify('画像を削除しました。', { type: 'info' });
                    refresh();
                  }
                }}
                confirmTitle="画像を削除"
                confirmContent="この画像を削除します。よろしいですか？"
              />
            }
          >
            {/* 画像プレビュー（サムネイル） */}
            <Box
              component="img"
              src={imageData.src}
              title={imageData.title}
              sx={{
                maxWidth: 150,
                maxHeight: 150,
                width: 'auto',
                height: 'auto',
                border: '1px solid #eee',
                mr: 2, // 画像とファイル名の間隔
                cursor: 'pointer'
              }}
              onClick={() => onImageClick(imageData.src, imageData.title)}
            />
            {/* ファイル名 */}
            <ListItemText
              primary={
                <Typography
                  component="span"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 4, // 4行で切り捨て
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-all',
                    pr: 4 // 削除ボタンとの間隔を確保
                  }}
                >
                  {imageData.title}
                </Typography>
              }
              sx={{
                flex: '1 1 auto',
                minWidth: 0 // flexアイテムが縮小できるようにする
              }}
            />
          </ListItem>
        );
      })}
    </>
  );
};

// =================================================================
// ショップ情報（テキスト）用のコンポーネント
// =================================================================
export const ShopInfoEdit = () => {
  const notify = useNotify();
  const refresh = useRefresh();

  return (
    <Edit
      resource="client-pr"
      id="me" // 自分の情報（ID: me）を編集
      title="ショップ情報"
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify('ショップ情報を更新しました。', { type: 'info' });
          refresh();
        }
      }}
    >
      {/* --- テキスト入力フォーム --- */}
      <SimpleForm toolbar={<CustomToolbar />}>
        <Card sx={{ flex: 1, minWidth: 0, width: '100%', mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              承認条件
            </Typography>
            <TextInput source="approvalConditions" label={false} multiline fullWidth rows={15} />
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 0, width: '100%', mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              否認条件
            </Typography>
            <TextInput source="disapprovalConditions" label={false} multiline fullWidth rows={15} />       
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 0, width: '100%', mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              ショップ情報
            </Typography>
            <TextInput source="shopInformation" label={false} multiline fullWidth rows={15} />
          </CardContent>
        </Card>
      </SimpleForm>
      {/* --- 画像管理コンポーネントの呼び出し --- */}
      <Box>
        <ImageManager />
      </Box>
    </Edit>
  );
};
